import React from 'react';

import { gql, useQuery } from '@apollo/client';
import { MenuItem, Select, Typography } from '@mui/material';
import { filter, find, includes, isEmpty, map } from 'lodash';
import { createScopedI18n } from '../../../i18n/i18n';

const usersDetailPageI18n = createScopedI18n('pages.users_detail');

const getChainRestaurantsGql = gql`
  query getChainRestaurants {
    user {
      id
      restaurants {
        id
        name
        branchName
      }
    }
  }
`;

type SelectorRestaurant = {
  id: string;
  name?: string | null;
  branchName?: string | null;
};

export type ChainRestaurantSelectorProps = {
  restaurant?: SelectorRestaurant | null;
  onChange?: (restaurant: SelectorRestaurant) => void;
  excludeRestaurantIds?: string[] | null;
} & Omit<React.ComponentProps<typeof Select>, 'value' | 'onChange'>;

const ChainRestaurantSelector = ({
  restaurant,
  onChange,
  excludeRestaurantIds,
  ...selectorProps
}: ChainRestaurantSelectorProps) => {
  const { data: chainRestaurantsData, loading: chainRestaurantsLoading } = useQuery(getChainRestaurantsGql, {
    fetchPolicy: 'cache-first',
  });

  const restaurants: SelectorRestaurant[] = React.useMemo(
    () => filter(chainRestaurantsData?.user?.restaurants ?? [], ({ id }) => !includes(excludeRestaurantIds, id)),
    [chainRestaurantsData?.user?.restaurants, excludeRestaurantIds],
  );

  const handleChange = React.useCallback<NonNullable<React.ComponentProps<typeof Select>['onChange']>>(
    (event) => {
      const selectedRestaurant = find(restaurants, ({ id }) => id === event.target.value);
      if (selectedRestaurant && onChange) {
        onChange(selectedRestaurant);
      }
    },
    [onChange, restaurants],
  );

  const placeholder = chainRestaurantsLoading
    ? usersDetailPageI18n('loading')
    : usersDetailPageI18n('select_restaurant');
  const displayValue = React.useCallback(
    (name?: string | null, branchName?: string | null) => `${name ?? ''} ${branchName ?? ''}`.trim(),
    [],
  );

  return (
    <Select
      value={restaurant?.id ?? ''}
      onChange={handleChange}
      disabled={chainRestaurantsLoading}
      displayEmpty
      renderValue={() =>
        restaurant ? (
          <Typography noWrap>{displayValue(restaurant.name, restaurant.branchName)}</Typography>
        ) : (
          <Typography noWrap color="text.disabled">
            {placeholder}
          </Typography>
        )
      }
      {...selectorProps}
      MenuProps={{
        anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
        transformOrigin: { vertical: 'top', horizontal: 'left' },
        PaperProps: { sx: { maxHeight: 'calc(50vh - 96px)' } },
        ...(selectorProps?.MenuProps ?? {}),
      }}
    >
      {isEmpty(restaurants) ? (
        <MenuItem disabled>
          {chainRestaurantsLoading ? usersDetailPageI18n('loading') : usersDetailPageI18n('blank_restaurants_selector')}
        </MenuItem>
      ) : (
        map(restaurants, (rest) => (
          <MenuItem key={rest.id} value={rest.id}>
            {displayValue(rest.name, rest.branchName)}
          </MenuItem>
        ))
      )}
    </Select>
  );
};

export default ChainRestaurantSelector;
